/**
 * Reusable FilterChips component for displaying active filters across all pages.
 * Used by Dashboard, LeadsReports, ProposalsReports, ProjectsReports, ProspectsReports.
 * 
 * Props:
 * - activeFilterTags: Array of {key, label, color, icon, canRemove}
 * - onRemoveFilter: Function(tagKey) - called when X clicked on a tag
 * - onResetFilters: Function() - called when Reset All clicked
 * - compact: Boolean (default false) - if true, shows inline without separator
 */
import React from 'react';

const FilterChips = ({ activeFilterTags = [], onRemoveFilter, onResetFilters, compact = false }) => {
  if (!activeFilterTags || activeFilterTags.length === 0) {
    return null;
  }

  return (
    <div className="dashboard-filter-chips" style={compact ? { marginBottom: '12px' } : {}}>
      <span className="filter-active-label">Active filters:</span>
      {activeFilterTags.map(tag => (
        <span key={tag.key} className={`filter-chip filter-chip-${tag.color}`}>
          <span>{tag.icon}</span>
          <span>{tag.label}</span>
          {/* Allow removing non-locked filters (canRemove defaults to true if not specified) */}
          {(tag.canRemove !== false) && (
            <span
              className="filter-chip-close"
              role="button"
              title={`Remove ${tag.label} filter`}
              onClick={() => onRemoveFilter && onRemoveFilter(tag.key)}
            >×</span>
          )}
        </span>
      ))}
      {onResetFilters && (
        <button className="filter-reset-btn" onClick={onResetFilters} title="Reset all filters to default">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
            <path d="M3 3v5h5"/>
          </svg>
          Reset all
        </button>
      )}
      
      <style jsx>{`
        .dashboard-filter-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          align-items: center;
          padding: 8px 0 4px 0;
        }
        .filter-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 10px 3px 8px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          line-height: 1.5;
          cursor: default;
          user-select: none;
          transition: all 0.15s ease;
          border: 1px solid transparent;
        }
        .filter-chip-blue   { background: #dbeafe; color: #1d4ed8; border-color: #bfdbfe; }
        .filter-chip-purple { background: #ede9fe; color: #6d28d9; border-color: #ddd6fe; }
        .filter-chip-teal   { background: #ccfbf1; color: #0f766e; border-color: #99f6e4; }
        .filter-chip-orange { background: #ffedd5; color: #c2410c; border-color: #fed7aa; }
        .filter-chip-cyan   { background: #cffafe; color: #0e7490; border-color: #a5f3fc; }
        .filter-chip-green  { background: #dcfce7; color: #15803d; border-color: #bbf7d0; }
        .filter-chip-red    { background: #fee2e2; color: #b91c1c; border-color: #fecaca; }
        .filter-chip-close {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: rgba(0,0,0,0.12);
          cursor: pointer;
          margin-left: 2px;
          font-size: 10px;
          line-height: 1;
          flex-shrink: 0;
        }
        .filter-chip-close:hover {
          background: rgba(0,0,0,0.25);
        }
        .filter-reset-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          border: 1px dashed #cbd5e1;
          background: transparent;
          color: #64748b;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .filter-reset-btn:hover {
          background: #f1f5f9;
          border-color: #94a3b8;
          color: #334155;
        }
        .filter-active-label {
          font-size: 11px;
          color: #94a3b8;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          flex-shrink: 0;
        }
      `}</style>
    </div>
  );
};

export default FilterChips;
